import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Sparkles, Loader2 } from "lucide-react";

const ORDER = [
  "flyer_basic", "flyer_premium", "flyer_signature",
  "voice_standard",
  "video_short", "video_premium", "video_cinema",
];

const FEATURES = {
  flyer_basic: ["1 flyer haute définition", "Style au choix", "Export PNG/JPG", "Tous droits commerciaux"],
  flyer_premium: ["3 versions de flyers", "Logo intégré premium", "Édition assistée", "Export PNG/JPG/PDF"],
  flyer_signature: ["5 flyers Or & Noir", "Direction artistique sur mesure", "Pack réseaux sociaux", "Retouches illimitées"],
  voice_standard: ["Voix off cinéma", "Choix homme/femme/enfant", "Effets dramatiques", "Export MP3 HD"],
  video_short: ["Vidéo Sora 2 — 4s", "Format au choix", "Voix off incluse", "Export MP4 HD"],
  video_premium: ["Vidéo Sora 2 — 8s", "Multi-séquences assemblées", "Voix off + musique", "2 formats inclus"],
  video_cinema: ["Vidéo Sora 2 Pro — 12s", "Cinéma premium", "Voix off premium clonée", "Pack social complet"],
};

const HIGHLIGHT = "flyer_premium";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/payments/packages"); setPacks(data.packages); }
      catch (err) { console.warn("packages load failed:", err?.message); }
    })();
  }, []);

  const buy = async (pkgId) => {
    if (!user) { navigate("/login?mode=signup"); return; }
    setLoadingKey(pkgId);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur paiement");
      setLoadingKey(null);
    }
  };

  const grouped = {
    flyer: ORDER.filter(k => k.startsWith("flyer")),
    voice: ORDER.filter(k => k.startsWith("voice")),
    video: ORDER.filter(k => k.startsWith("video")),
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-center mb-14">
        <span className="label-mono">Tarification transparente</span>
        <h1 className="mt-3 text-5xl font-heading tracking-tighter">Des packs <span className="gold-text">pour chaque ambition</span></h1>
        <p className="mt-4 text-white/60 max-w-xl mx-auto">Payez à la création, sans abonnement. Vos droits commerciaux sont inclus.</p>
      </div>

      {Object.entries(grouped).map(([k, ids]) => (
        <section key={k} className="mb-16">
          <h2 className="font-heading text-2xl mb-6 tracking-tight">
            {k === "flyer" && "Flyers"}
            {k === "voice" && "Voix off"}
            {k === "video" && "Vidéos publicitaires"}
          </h2>
          <div className={`grid gap-5 ${ids.length === 3 ? "md:grid-cols-3" : "md:grid-cols-1 max-w-md"}`}>
            {ids.map(id => {
              const p = packs[id];
              if (!p) return null;
              const highlight = id === HIGHLIGHT;
              return (
                <div key={id} className={`relative ${highlight ? "gold-border-gradient" : "studio-card"} p-7 flex flex-col`}>
                  {highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gold text-black text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1">
                      <Crown className="w-3 h-3" /> Recommandé
                    </div>
                  )}
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-heading text-2xl">{p.name}</h3>
                  </div>
                  <div className="mt-5 flex items-end gap-1">
                    <span className="text-5xl font-heading gold-text font-semibold">{p.amount.toFixed(0)}</span>
                    <span className="text-white/50 mb-2">€</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {FEATURES[id].map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" /><span className="text-white/80">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`buy-${id}`} onClick={()=>buy(id)} disabled={loadingKey===id}
                    className={`${highlight ? "gold-btn" : "ghost-btn"} mt-7 w-full !py-3 disabled:opacity-50`}>
                    {loadingKey === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Acheter <Sparkles className="w-4 h-4" /></>}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div className="text-center mt-10 text-white/50 text-sm">
        Paiement sécurisé via Stripe · Cartes bancaires acceptées · TVA incluse
      </div>
    </div>
  );
}
